import { addCidade, getCidades } from './experienciasStorage';
import { tipoStatusProgramadoDominanteNoDia, ymdReferenciaAgendaStatusLocal } from '../../../shared/lib/statusProgramadoNoticia';
import { decodeLiteralUnicodeEscapes, decodeLiteralUnicodeEscapesDeep } from '../../../shared/lib/decodeLiteralUnicodeEscapes';

export interface NewsCategory {
  id: string;
  name: string;
}

export interface NewsSubcategory {
  id: string;
  categoryId: string;
  name: string;
}

export interface NewsResponsibleUser {
  userId: string;
  name: string;
  email: string;
  /** Telefone (ex.: WhatsApp), opcional — preenchido a partir do perfil comércio quando aplicável. */
  phone?: string;
}

/** Tipo de destaque no layout: matéria principal (destaque), slide principal (carrossel), ou normal. */
export type NewsLayoutType = 'normal' | 'matéria_principal' | 'slide_principal';

/** Âmbito geográfico da notícia (valores alinhados ao cadastro de cidades, exceto `global`). */
export type NewsLocalizacaoEscopo = 'global' | 'pais' | 'estado' | 'regiao' | 'cidade';

/** Referência múltipla na matéria (admin): estabelecimento, parceiros, hospedagem ou evento. */
export type NoticiaAssociacaoTipo =
  | 'estabelecimento'
  | 'parceiro_guia'
  | 'parceiro_agencia'
  | 'hospedagem'
  | 'evento';

export interface NoticiaAssociacaoRef {
  tipo: NoticiaAssociacaoTipo;
  id: string;
}

/** Lista unificada a partir do artigo (`associacoes` ou campos legacy). */
export function associacoesFromArticle(article: NewsArticle): NoticiaAssociacaoRef[] {
  if (article.associacoes && article.associacoes.length > 0) {
    return article.associacoes;
  }
  const out: NoticiaAssociacaoRef[] = [];
  if (article.estabelecimentoId?.trim()) out.push({ tipo: 'estabelecimento', id: article.estabelecimentoId.trim() });
  if (article.parceiroGuiaId?.trim()) out.push({ tipo: 'parceiro_guia', id: article.parceiroGuiaId.trim() });
  if (article.parceiroAgenciaId?.trim()) out.push({ tipo: 'parceiro_agencia', id: article.parceiroAgenciaId.trim() });
  return out;
}

/** Primeiro de cada tipo “único” nos campos legacy (compat). */
export function legacyAssociacaoSingleIds(associacoes: NoticiaAssociacaoRef[]) {
  const est = associacoes.find((a) => a.tipo === 'estabelecimento');
  const pg = associacoes.find((a) => a.tipo === 'parceiro_guia');
  const pa = associacoes.find((a) => a.tipo === 'parceiro_agencia');
  return {
    estabelecimentoId: est?.id,
    parceiroGuiaId: pg?.id,
    parceiroAgenciaId: pa?.id,
  };
}

export interface NewsArticle {
  id: string;
  title: string;
  subtitle: string;
  summary: string;
  content: string;
  categoryId: string;
  /** Primeira subcategoria (espelho de `subcategoryIds[0]`; compatível com dados antigos só com este campo). */
  subcategoryId?: string;
  /** Uma ou mais subcategorias da matéria. */
  subcategoryIds?: string[];
  cityId: string;
  /** Âmbito da localização; ausente em dados antigos = tratar como `cidade` quando `cityId` existir. */
  localizacaoEscopo?: NewsLocalizacaoEscopo;
  /** Preenchidos conforme o escopo (ex.: país escolhido na lista derivada das cidades). */
  localizacaoPais?: string;
  localizacaoEstado?: string;
  localizacaoRegiao?: string;
  coverImage: string;
  images: string[];
  /** Tipo de layout/destaque: matéria principal, slide principal ou normal. */
  layoutType?: NewsLayoutType;
  /** Data de validade do destaque (ISO). Ao expirar, a matéria volta a ser tratada como normal. */
  layoutExpiresAt?: string;
  /** Destaque na subcategoria (página de categoria). Máx. 8 por subcategoria. */
  destaqueSubcategoria?: boolean;
  /** Data de validade do destaque subcategoria (ISO). Ao expirar, desmarca. */
  destaqueSubcategoriaExpiresAt?: string;
  /** Destaque promocional NaviEX (uso no app; validade obrigatória quando ativo). */
  destaqueNaviex?: boolean;
  /** Data de validade do destaque NaviEX (ISO). Ao expirar, desmarca. */
  destaqueNaviexExpiresAt?: string;
  /** Tags para categorização e busca. */
  tags?: string[];
  /** @deprecated Parceiros/estabelecimento passam a ser indicados em `responsibles`. Gravar vazio ao salvar no admin. */
  estabelecimentoId?: string;
  /** @deprecated Ver `responsibles`. */
  parceiroGuiaId?: string;
  /** @deprecated Ver `responsibles`. */
  parceiroAgenciaId?: string;
  /** @deprecated Preferir `responsibles`. Mantido só para leitura de dados antigos até regravar. */
  associacoes?: NoticiaAssociacaoRef[];
  /** Responsáveis comerciais / contactos da matéria (substitui o antigo «Associar Parceiro» no admin). */
  responsibles: NewsResponsibleUser[];
  /** Status ativo/inativo. Se desativada, não aparece no cliente. Default: ativada */
  statusPadrao?: 'ativada' | 'desativada';
  /** Períodos programados: visibilidade e/ou destaque na data. Sobrescreve statusPadrao na data (exceto tipos de destaque só alteram exibição conforme regra do app). */
  periodosStatus?: {
    de: string;
    ate: string;
    tipo: 'ativada' | 'desativada' | 'naviex' | 'principal' | 'categoria';
  }[];
  /** Histórico de agendamentos na UI: `modo` período = intervalo; permanente = alteração do status padrão (De = Até). */
  statusProgramadoHistorico?: {
    quando: string;
    dia: string;
    tipo: 'ativada' | 'desativada' | 'naviex' | 'principal' | 'categoria';
    modo?: 'periodo' | 'permanente';
    ate?: string;
  }[];
  /** Admin: rascunho até publicar; omitido = publicado (compatível com dados antigos); lixeira = removida da vitrine até restaurar ou apagar. */
  publicacaoStatus?: 'rascunho' | 'publicado' | 'lixeira';
  createdAt: string;
  updatedAt: string;
}

/** Une `subcategoryIds` e o campo legado `subcategoryId` numa lista deduplicada (ordem preservada). */
export function mergeNewsArticleSubcategoryIds(
  article: Pick<NewsArticle, 'subcategoryId' | 'subcategoryIds'>
): string[] {
  const fromArr = (article.subcategoryIds ?? []).map((id) => String(id).trim()).filter(Boolean);
  const leg = article.subcategoryId?.trim();
  const out: string[] = [];
  const seen = new Set<string>();
  for (const id of fromArr) {
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  if (leg && !seen.has(leg)) {
    out.unshift(leg);
    seen.add(leg);
  }
  return out;
}

/** Garante `subcategoryIds` + `subcategoryId` coerentes antes de gravar ou após ler. */
export function normalizeNewsArticleForStore(article: NewsArticle): NewsArticle {
  const ids = mergeNewsArticleSubcategoryIds(article);
  const decoded = decodeLiteralUnicodeEscapesDeep(article);
  return {
    ...decoded,
    subcategoryIds: ids.length > 0 ? ids : undefined,
    subcategoryId: ids[0] ?? '',
  };
}

/**
 * Status efetivo numa data de agenda (YYYY-MM-DD): `periodosStatus` que cubrem o dia;
 * em conflito, vence o tipo pela hierarquia (Naviex > Principal > Categoria > Ativada > Desativada).
 * Sem `data`, usa o dia de agenda local (corte 00:10:10). Fora de períodos, usa `statusPadrao`.
 * Tipos de destaque contam como **ativada** para visibilidade.
 */
export function getNewsStatusEfetivo(
  article: {
    statusPadrao?: 'ativada' | 'desativada';
    periodosStatus?: { de: string; ate: string; tipo: string }[];
  },
  data?: string
): 'ativada' | 'desativada' {
  const dia = data ?? ymdReferenciaAgendaStatusLocal();
  const tipo = tipoStatusProgramadoDominanteNoDia(article.periodosStatus, dia);
  if (!tipo) return article.statusPadrao ?? 'ativada';
  if (tipo === 'desativada') return 'desativada';
  return 'ativada';
}

/** True se a notícia estiver efetivamente ativa na data (sempre via períodos programados + status padrão). Rascunhos nunca contam como ativos. */
export function isNewsAtiva(
  article: {
    publicacaoStatus?: 'rascunho' | 'publicado' | 'lixeira';
    statusPadrao?: 'ativada' | 'desativada';
    periodosStatus?: { de: string; ate: string; tipo: string }[];
  },
  data?: string
): boolean {
  if (article.publicacaoStatus === 'rascunho' || article.publicacaoStatus === 'lixeira') return false;
  return getNewsStatusEfetivo(article, data) === 'ativada';
}

const KEY_NEWS_ARTICLES = 'naviex-admin-news-articles';
const KEY_NEWS_CATEGORIES = 'naviex-admin-news-categories';
const KEY_NEWS_SUBCATEGORIES = 'naviex-admin-news-subcategories';
const KEY_NEWS_LIMITS_BY_CITY = 'naviex-admin-news-limits-by-city';
const KEY_NEWS_TAGS = 'naviex-admin-news-tags';
const KEY_GUIA_REGISTERED_USERS = 'guiaRegisteredUsers';

/** Limites de destaques por cidade. Cada cidade tem seu próprio limite. */
export interface NewsLimitsByCity {
  maxDestaquesPrincipal: number;
  maxDestaquesSubcategoria: number;
}

const DEFAULT_LIMITS: NewsLimitsByCity = { maxDestaquesPrincipal: 8, maxDestaquesSubcategoria: 8 };

export function getNewsLimitsByCity(): Record<string, NewsLimitsByCity> {
  return loadJson<Record<string, NewsLimitsByCity>>(KEY_NEWS_LIMITS_BY_CITY, {});
}

export function getNewsLimitForCity(cityId: string, type: 'principal' | 'subcategoria'): number {
  const limits = getNewsLimitsByCity();
  const cityLimits = limits[cityId];
  if (!cityLimits) return type === 'principal' ? DEFAULT_LIMITS.maxDestaquesPrincipal : DEFAULT_LIMITS.maxDestaquesSubcategoria;
  return type === 'principal' ? cityLimits.maxDestaquesPrincipal : cityLimits.maxDestaquesSubcategoria;
}

export function saveNewsLimitsForCity(cityId: string, limits: NewsLimitsByCity): void {
  const current = getNewsLimitsByCity();
  saveJson(KEY_NEWS_LIMITS_BY_CITY, { ...current, [cityId]: limits });
}

/** Categorias para teste (apenas 3). */
const FALLBACK_CATEGORY_NAMES = ['Gastronomia', 'Cultura', 'Eventos'];
const FALLBACK_SUBCATEGORY_MAP: Record<string, string[]> = {
  Gastronomia: ['Restaurantes', 'Bares', 'Cafés'],
  Cultura: ['Museus', 'Arte', 'História'],
  Eventos: ['Festas', 'Shows', 'Feiras'],
};

type GuiaRegisteredUser = {
  userId?: string;
  name?: string;
  email?: string;
  roles?: string[];
};

function normalizeText(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (decodeLiteralUnicodeEscapesDeep(JSON.parse(raw)) as T) : fallback;
  } catch {
    return fallback;
  }
}

function saveJson(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

const OLD_EXPERIENCE_CATEGORY_NAMES = ['natureza', 'city tour', 'aventura', 'terapias'];

function ensureSeeds(): void {
  let currentCategories = loadJson<NewsCategory[]>(KEY_NEWS_CATEGORIES, []);
  let currentSubcategories = loadJson<NewsSubcategory[]>(KEY_NEWS_SUBCATEGORIES, []);

  const hasOldExperienceCategories = currentCategories.some((c) =>
    OLD_EXPERIENCE_CATEGORY_NAMES.includes(c.name.trim().toLowerCase())
  );
  if (hasOldExperienceCategories) {
    currentCategories = [];
    currentSubcategories = [];
  }

  if (currentCategories.length > 0 && currentSubcategories.length > 0) return;

  const fallbackCategories: NewsCategory[] = FALLBACK_CATEGORY_NAMES.map((name) => ({
    id: `cat-${slugify(name)}`,
    name,
  }));
  const fallbackSubcategories: NewsSubcategory[] = [];
  fallbackCategories.forEach((category) => {
    const subNames = FALLBACK_SUBCATEGORY_MAP[category.name] ?? ['Geral'];
    subNames.forEach((subName) => {
      fallbackSubcategories.push({
        id: `sub-${slugify(category.name)}-${slugify(subName)}`,
        categoryId: category.id,
        name: subName,
      });
    });
  });

  saveJson(KEY_NEWS_CATEGORIES, fallbackCategories);
  saveJson(KEY_NEWS_SUBCATEGORIES, fallbackSubcategories);
}

export function getNewsCategories(): NewsCategory[] {
  ensureSeeds();
  return loadJson<NewsCategory[]>(KEY_NEWS_CATEGORIES, []).map((c) => ({
    ...c,
    name: decodeLiteralUnicodeEscapes(c.name),
  }));
}

export function addNewsCategory(name: string): NewsCategory {
  const normalized = name.trim();
  const list = getNewsCategories();
  const exists = list.some((item) => normalizeText(item.name) === normalizeText(normalized));
  if (exists) {
    throw new Error('Esta categoria já existe.');
  }
  const next: NewsCategory = {
    id: `cat-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: normalized,
  };
  saveJson(KEY_NEWS_CATEGORIES, [...list, next]);
  return next;
}

export function updateNewsCategory(id: string, name: string): void {
  const normalized = name.trim();
  if (!normalized) throw new Error('Informe o nome da categoria.');
  const list = getNewsCategories();
  const index = list.findIndex((item) => item.id === id);
  if (index < 0) throw new Error('Categoria não encontrada.');
  const exists = list.some(
    (item) => item.id !== id && normalizeText(item.name) === normalizeText(normalized)
  );
  if (exists) throw new Error('Esta categoria já existe.');
  list[index] = { ...list[index], name: normalized };
  saveJson(KEY_NEWS_CATEGORIES, list);
}

export function getNewsSubcategories(): NewsSubcategory[] {
  ensureSeeds();
  return loadJson<NewsSubcategory[]>(KEY_NEWS_SUBCATEGORIES, []).map((s) => ({
    ...s,
    name: decodeLiteralUnicodeEscapes(s.name),
  }));
}

export function getNewsSubcategoriesByCategory(categoryId: string): NewsSubcategory[] {
  if (!categoryId) return [];
  return getNewsSubcategories().filter((item) => item.categoryId === categoryId);
}

export function addNewsSubcategory(categoryId: string, name: string): NewsSubcategory {
  const normalized = name.trim();
  const subcategories = getNewsSubcategories();
  const exists = subcategories.some(
    (item) => item.categoryId === categoryId && normalizeText(item.name) === normalizeText(normalized)
  );
  if (exists) {
    throw new Error('Esta subcategoria já existe para a categoria selecionada.');
  }
  const next: NewsSubcategory = {
    id: `sub-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    categoryId,
    name: normalized,
  };
  saveJson(KEY_NEWS_SUBCATEGORIES, [...subcategories, next]);
  return next;
}

export function updateNewsSubcategory(id: string, name: string, categoryId: string): void {
  const normalized = name.trim();
  if (!normalized) throw new Error('Informe o nome da subcategoria.');
  if (!categoryId.trim()) throw new Error('Selecione a categoria da subcategoria.');
  const list = getNewsSubcategories();
  const index = list.findIndex((item) => item.id === id);
  if (index < 0) throw new Error('Subcategoria não encontrada.');
  const exists = list.some(
    (item) =>
      item.id !== id &&
      item.categoryId === categoryId &&
      normalizeText(item.name) === normalizeText(normalized)
  );
  if (exists) throw new Error('Esta subcategoria já existe para a categoria selecionada.');
  list[index] = { ...list[index], name: normalized, categoryId };
  saveJson(KEY_NEWS_SUBCATEGORIES, list);
}

export async function removeNewsCategory(
  categoryId: string,
  options?: { newCategoryId: string; newSubcategoryId: string }
): Promise<{ ok: boolean; articlesCount: number }> {
  const articles = getNewsArticles();
  const artCount = articles.filter((a) => a.categoryId === categoryId).length;

  if (artCount > 0 && !options) {
    return { ok: false, articlesCount: artCount };
  }

  if (artCount > 0 && options) {
    articles
      .filter((a) => a.categoryId === categoryId)
      .forEach((a) =>
        saveNewsArticle(
          normalizeNewsArticleForStore({
            ...a,
            categoryId: options.newCategoryId,
            subcategoryIds: [options.newSubcategoryId],
            subcategoryId: options.newSubcategoryId,
          })
        )
      );
  }

  const cats = getNewsCategories().filter((c) => c.id !== categoryId);
  const subs = getNewsSubcategories().filter((s) => s.categoryId !== categoryId);
  saveJson(KEY_NEWS_CATEGORIES, cats);
  saveJson(KEY_NEWS_SUBCATEGORIES, subs);
  return { ok: true, articlesCount: 0 };
}

export async function removeNewsSubcategory(
  subcategoryId: string,
  options?: { newCategoryId: string; newSubcategoryId: string }
): Promise<{ ok: boolean; articlesCount: number }> {
  const articles = getNewsArticles();
  const artCount = articles.filter((a) => mergeNewsArticleSubcategoryIds(a).includes(subcategoryId)).length;

  if (artCount > 0 && !options) {
    return { ok: false, articlesCount: artCount };
  }

  if (artCount > 0 && options) {
    articles
      .filter((a) => mergeNewsArticleSubcategoryIds(a).includes(subcategoryId))
      .forEach((a) => {
        const nextIds = mergeNewsArticleSubcategoryIds(a)
          .map((id) => (id === subcategoryId ? options.newSubcategoryId : id))
          .filter(Boolean);
        const dedup = [...new Set(nextIds)];
        saveNewsArticle(
          normalizeNewsArticleForStore({
            ...a,
            categoryId: options.newCategoryId,
            subcategoryIds: dedup,
            subcategoryId: dedup[0] ?? '',
          })
        );
      });
  }

  const subs = getNewsSubcategories().filter((s) => s.id !== subcategoryId);
  saveJson(KEY_NEWS_SUBCATEGORIES, subs);
  return { ok: true, articlesCount: 0 };
}

export function getNewsArticles(): NewsArticle[] {
  return loadJson<NewsArticle[]>(KEY_NEWS_ARTICLES, []).map((raw) => normalizeNewsArticleForStore(raw as NewsArticle));
}

export function getNewsArticleById(id: string): NewsArticle | null {
  return getNewsArticles().find((item) => item.id === id) ?? null;
}

export function addNewsArticle(data: Omit<NewsArticle, 'id' | 'createdAt' | 'updatedAt'>): NewsArticle {
  const now = new Date().toISOString();
  const rawList = loadJson<NewsArticle[]>(KEY_NEWS_ARTICLES, []);
  const next: NewsArticle = normalizeNewsArticleForStore({
    ...(data as NewsArticle),
    id: `news-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    createdAt: now,
    updatedAt: now,
  });
  saveJson(KEY_NEWS_ARTICLES, [...rawList, next]);
  return next;
}

export function saveNewsArticle(article: NewsArticle): NewsArticle {
  const list = loadJson<NewsArticle[]>(KEY_NEWS_ARTICLES, []).map((raw) => normalizeNewsArticleForStore(raw as NewsArticle));
  const idx = list.findIndex((item) => item.id === article.id);
  if (idx < 0) {
    throw new Error('Notícia não encontrada para atualização.');
  }
  const updated = normalizeNewsArticleForStore({
    ...list[idx],
    ...article,
    updatedAt: new Date().toISOString(),
  });
  const next = [...list];
  next[idx] = updated;
  saveJson(KEY_NEWS_ARTICLES, next);
  return updated;
}

export function removeNewsArticle(id: string): void {
  saveJson(
    KEY_NEWS_ARTICLES,
    loadJson<NewsArticle[]>(KEY_NEWS_ARTICLES, [])
      .map((raw) => normalizeNewsArticleForStore(raw as NewsArticle))
      .filter((item) => item.id !== id)
  );
}

// ——— Tags de notícias (lista global separada das tags de experiências) ———

function sortTagList(list: string[]): string[] {
  return [...new Set(list.map((x) => x.trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

/** Lista global de tags de notícias (persistida; artigos referenciam por nome). */
export function getNewsTagRegistry(): string[] {
  try {
    const data = loadJson<string[]>(KEY_NEWS_TAGS, []);
    return Array.isArray(data) ? sortTagList(data) : [];
  } catch {
    return [];
  }
}

function saveNewsTagRegistryList(list: string[]): void {
  saveJson(KEY_NEWS_TAGS, sortTagList(list));
}

/** Adiciona tag ao cadastro global de tags de notícias (trim; não mistura com experiências). */
export function addNewsTagToRegistry(name: string): void {
  const t = name.trim();
  if (!t) return;
  const list = getNewsTagRegistry();
  if (list.includes(t)) return;
  saveNewsTagRegistryList([...list, t]);
}

/** Renomeia na lista global e em todos os artigos que usam a tag. */
export function renameNewsTagInRegistry(oldName: string, newName: string): void {
  const t = newName.trim();
  if (!t || t === oldName) return;
  const list = getNewsTagRegistry().filter((x) => x !== oldName);
  if (!list.includes(t)) list.push(t);
  saveNewsTagRegistryList(list);
  getNewsArticles().forEach((article) => {
    const tags = article.tags ?? [];
    if (!tags.includes(oldName)) return;
    saveNewsArticle({
      ...article,
      tags: tags.map((tag) => (tag === oldName ? t : tag)),
    });
  });
}

/** Remove só do cadastro global (quando nenhum artigo usa a tag). */
export function removeNewsTagFromRegistryOnly(name: string): void {
  saveNewsTagRegistryList(getNewsTagRegistry().filter((x) => x !== name));
}

/** Remove do cadastro global e de todos os artigos. */
export function removeNewsTagCompletely(name: string): void {
  removeNewsTagFromRegistryOnly(name);
  getNewsArticles().forEach((article) => {
    const tags = article.tags ?? [];
    if (!tags.includes(name)) return;
    saveNewsArticle({
      ...article,
      tags: tags.filter((tag) => tag !== name),
    });
  });
}

export function getGuiaRegisteredUsersForSync(): Record<string, GuiaRegisteredUser> {
  return loadJson(KEY_GUIA_REGISTERED_USERS, {});
}

/** Usuários com perfil COMERCIO_RESTAURANTE em `guiaRegisteredUsers` (cadastros reais no app guia / sync). Sem dados fictícios. */
export function getComercioRestauranteUsers(): NewsResponsibleUser[] {
  const resultByEmail = new Map<string, NewsResponsibleUser>();
  const registered = loadJson<Record<string, GuiaRegisteredUser>>(KEY_GUIA_REGISTERED_USERS, {});
  Object.values(registered).forEach((user) => {
    const roles = Array.isArray(user.roles) ? user.roles : [];
    if (!roles.includes('COMERCIO_RESTAURANTE')) return;
    const email = (user.email ?? '').trim().toLowerCase();
    if (!email) return;
    resultByEmail.set(email, {
      userId: user.userId ?? `comercio-${slugify(email)}`,
      name: user.name?.trim() || email,
      email,
    });
  });

  return Array.from(resultByEmail.values()).sort((a, b) => a.name.localeCompare(b.name));
}
